import { buildFieldIndex } from 'helpers/inspectionResourceHelper';
import { qualifySuuid, bareSuuid } from 'components/FieldColumns/sourceGroups';

// The API's built-in column keys per tab (InspectCustomColumns.HistoryBuiltInKeys /
// ScheduledBuiltInKeys), in table order. `type` only shapes the preview's placeholder cells.
export var INSPECT_BUILTIN_COLUMNS = {
  history: [
    { key: 'asset-name', labelKey: 'inspect:builtinColumnAssetName', type: 'text' },
    { key: 'inspection', labelKey: 'inspect:builtinColumnInspection', type: 'text' },
    { key: 'timestamp', labelKey: 'inspect:dateColumnLabel', type: 'dateTime' },
    { key: 'user', labelKey: 'inspect:builtinColumnLastChangedBy', type: 'text' },
    { key: 'result', labelKey: 'inspect:builtinColumnResult', type: 'text' },
  ],
  scheduled: [
    { key: 'asset-name', labelKey: 'inspect:builtinColumnAssetName', type: 'text' },
    { key: 'inspection', labelKey: 'inspect:builtinColumnInspection', type: 'text' },
    { key: 'due-date', labelKey: 'inspect:builtinColumnDueDate', type: 'dateTime' },
    { key: 'status', labelKey: 'inspect:builtinColumnStatus', type: 'text' },
    { key: 'frequency', labelKey: 'inspect:builtinColumnFrequency', type: 'text' },
    { key: 'last-inspected', labelKey: 'inspect:builtinColumnLastInspected', type: 'dateTime' },
  ],
};

export var CUSTOM_COLUMN_LISTS = { history: 'historyCustomColumns', scheduled: 'scheduledCustomColumns' };
export var RECORD_SOURCE_BY_TAB = { history: 'recordEntry', scheduled: 'recordTemplate' };

var START = 'start';
var RESERVED_PREFIXES = ['__inspect', '__hidden_inspect'];
var RECORDS_TYPE = 'records';

export function customColumnKey(stored) {
  return 'cf.' + stored.source + '.' + stored.fieldSUUID + '.' + stored.fieldAttribute;
}

export function builtinRows(tab, t) {
  return INSPECT_BUILTIN_COLUMNS[tab].map(function (col) {
    return { cuuid: 'builtin:' + col.key, key: col.key, title: t(col.labelKey), type: col.type, locked: true };
  });
}

// Lays stored custom rows among the locked built-in rows: "start" before the first built-in, a
// known anchor directly after its built-in (after earlier customs on the same anchor), an unknown
// anchor at the end.
export function interleaveColumns(fixedRows, customRows) {
  var atStart = [];
  var atEnd = [];
  var byAnchor = Object.create(null);
  fixedRows.forEach(function (row) { byAnchor[row.key] = []; });
  (customRows || []).forEach(function (row) {
    if (row.afterColumn === START) atStart.push(row);
    else if (byAnchor[row.afterColumn]) byAnchor[row.afterColumn].push(row);
    else atEnd.push(row);
  });
  var out = atStart.slice();
  fixedRows.forEach(function (row) {
    out.push(row);
    out = out.concat(byAnchor[row.key]);
  });
  return out.concat(atEnd);
}

export function deriveAfterColumns(rows) {
  var anchor = START;
  var out = [];
  rows.forEach(function (row) {
    if (row.locked) {
      anchor = row.key;
      return;
    }
    out.push(Object.assign({}, row, { afterColumn: anchor }));
  });
  return out;
}

export function toPickerColumn(stored, t) {
  var path = Array.isArray(stored.fieldPath) ? stored.fieldPath.slice() : [];
  var title = stored.fieldTitle || '';
  var orphaned = !!stored.isOrphaned;
  var qualified = qualifySuuid(stored.source, stored.fieldSUUID);
  return {
    cuuid: customColumnKey(stored),
    suuid: qualified,
    fieldSUUID: qualified,
    fieldAttribute: stored.fieldAttribute,
    afterColumn: stored.afterColumn,
    source: stored.source,
    type: stored.fieldType,
    title: orphaned ? t('inspect:columnsOrphanTitle') : title,
    hierarchyPath: orphaned ? path.concat(title ? [title] : []) : path,
    orphaned: orphaned,
    storedPath: path,
    storedTitle: title,
  };
}

export function toStoredColumn(row) {
  return {
    source: row.source,
    fieldSUUID: bareSuuid(row.fieldSUUID || row.suuid),
    fieldAttribute: row.fieldAttribute,
    afterColumn: row.afterColumn,
    fieldPath: row.storedPath ? row.storedPath.slice() : (row.hierarchyPath || []).slice(),
    fieldTitle: row.storedTitle !== undefined ? row.storedTitle : row.title,
    fieldType: row.type,
    isOrphaned: !!row.orphaned,
  };
}

function isReservedTitle(title) {
  return !!title && RESERVED_PREFIXES.some(function (p) { return title.indexOf(p) === 0; });
}

// Copies of the settings fields tree for the two picker sections (R3). Never mutates the input.
// asset: everything a slate-sourced column can resolve to, without the inspected records field.
// records: the inspected records field's children.
export function buildColumnSourceTrees(settingsFields, recordsFieldKey) {
  // excludeKey drops the inspected records field wherever it sits (it can be inside a group).
  var prune = function (list, excludeKey) {
    var out = [];
    for (var i = 0; i < (list || []).length; i++) {
      var field = list[i];
      if (!field || field.meta || isReservedTitle(field.title)) continue;
      if (excludeKey && field.suuid === excludeKey) continue;
      var copy = Object.assign({}, field);
      if (field.type === RECORDS_TYPE) {
        delete copy.children;
      } else if (field.children) {
        copy.children = prune(field.children, excludeKey);
      }
      // A group left with no children is not an addable column (the API rejects group columns).
      if (field.type === 'group' && !(copy.children && copy.children.length)) continue;
      out.push(copy);
    }
    return out;
  };
  // The settings-fields API returns a records child's suuid as <recordsFieldPathHash><childSUUID>;
  // the Inspect API wants the bare child SUUID, so strip the records prefix from every node.
  var stripPrefix = function (list) {
    return list.map(function (field) {
      var copy = Object.assign({}, field);
      var suuid = field.suuid;
      if (typeof suuid === 'string' && recordsFieldKey && suuid.indexOf(recordsFieldKey) === 0) {
        copy.suuid = suuid.slice(recordsFieldKey.length);
      }
      if (field.children) copy.children = stripPrefix(field.children);
      return copy;
    });
  };
  var recordsField = recordsFieldKey ? buildFieldIndex(settingsFields)[recordsFieldKey] : null;
  var asset = prune(settingsFields, recordsFieldKey);
  return {
    records: recordsField ? stripPrefix(prune(recordsField.children)) : [],
    asset: asset,
  };
}
